import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { onIdTokenChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

/**
 * First-party activity beacon. Sends pageview + dwell events to POST /api/track
 * for signed-in users only. Identity is NOT sent in the body — the server derives
 * it from the verified token. Transport is `fetch(..., keepalive)` (not
 * sendBeacon) so the Authorization header can be attached and the request can
 * still complete during page unload. Renders nothing.
 *
 * The token is cached with its uid and refreshed via onIdTokenChanged (which also
 * fires on Firebase's ~hourly auto-refresh), so a dwell sent on pagehide after a
 * long single-page session still uses a valid token. The uid is compared before
 * sending so a stale token from a previous account can never misattribute the
 * new user's activity.
 *
 * Honest caveat: dwell is wall-clock time on a route while the tab is FOREGROUND
 * (we reset on visibility return and drop sub-second dwells), but idle time with
 * the tab focused still inflates it. No active-input idle detection in v1.
 */
const MIN_DWELL_MS = 1000;

export default function ActivityTracker() {
  const { user } = useAuth();
  const location = useLocation();
  const tokenRef = useRef<{ uid: string; token: string } | null>(null);
  const currentUidRef = useRef<string | null>(null);
  const pathRef = useRef<string | null>(null);
  const enteredAtRef = useRef<number>(Date.now());

  useEffect(() => {
    currentUidRef.current = user?.uid ?? null;
  }, [user]);

  // Keep the ID token (with its uid) fresh across sign-in/out and auto-refresh.
  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async (u) => {
      if (!u) {
        tokenRef.current = null;
        return;
      }
      const t = await u.getIdToken().catch(() => null);
      tokenRef.current = t ? { uid: u.uid, token: t } : null;
    });
    return unsub;
  }, []);

  const send = useCallback((type: 'pageview' | 'dwell', path: string, dwellMs?: number) => {
    // Drop trivial/noisy dwells (also suppresses the ~0ms second flush when
    // visibilitychange→hidden is immediately followed by pagehide).
    if (type === 'dwell' && (dwellMs == null || dwellMs < MIN_DWELL_MS)) return;
    const tok = tokenRef.current;
    // No token, or the cached token belongs to a different (previous) user.
    if (!tok || tok.uid !== currentUidRef.current) return;
    const body =
      dwellMs != null ? JSON.stringify({ type, path, dwellMs }) : JSON.stringify({ type, path });
    try {
      void fetch('/api/track', {
        method: 'POST',
        keepalive: true,
        credentials: 'omit',
        headers: { Authorization: `Bearer ${tok.token}`, 'Content-Type': 'application/json' },
        body,
      }).catch(() => {});
    } catch {
      /* best-effort; never throw into the app */
    }
  }, []);

  // Route change → dwell for the previous path + pageview for the new path.
  useEffect(() => {
    if (!user) {
      pathRef.current = null;
      return;
    }
    const now = Date.now();
    const path = location.pathname;
    const prev = pathRef.current;
    if (prev === path) return;
    if (prev) send('dwell', prev, Math.max(0, now - enteredAtRef.current));
    send('pageview', path);
    pathRef.current = path;
    enteredAtRef.current = now;
  }, [location.pathname, user, send]);

  // Tab hidden / page unload → flush dwell; returning to foreground resets the
  // clock so background time isn't counted.
  useEffect(() => {
    const flush = () => {
      if (!user || !pathRef.current) return;
      const now = Date.now();
      send('dwell', pathRef.current, Math.max(0, now - enteredAtRef.current));
      enteredAtRef.current = now;
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') flush();
      else enteredAtRef.current = Date.now();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', flush);
    };
  }, [user, send]);

  return null;
}
